import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'

import { useGetCreditBalance } from '../../api/generated/endpoints/credits/credits'
import { useGetMe } from '../../api/generated/endpoints/me/me'
import { OrphanedPaymentsNotice } from '../../components/OrphanedPaymentsNotice'
import { formatHours } from '../../lib/credits'
import { supabase } from '../../lib/supabase'

/**
 * The account page, and the fourth tab on a phone.
 *
 * It used to be the M0 acceptance criterion — a logged-in page proving a
 * protected endpoint worked through a generated hook — and it still showed the
 * raw user id and "Admin: yes". Now it is where everything that did not fit in
 * four tabs lives: buying hours, and the way into the facility screens.
 *
 * That is the deliberate trade a bottom tab bar makes. Four destinations, and
 * one of them has to be a hub.
 */
export const Route = createFileRoute('/_authed/me')({
  component: MePage,
})

const ROW = 'flex min-h-14 items-center justify-between gap-4 px-4'

function MePage() {
  const navigate = useNavigate()
  // Not destructured: see the note in routes/index.tsx about narrowing.
  const meQuery = useGetMe()
  const credit = useGetCreditBalance()

  async function handleSignOut() {
    await supabase.auth.signOut()
    void navigate({ to: '/', replace: true })
  }

  if (meQuery.isPending) return <p className="text-slate-600">Cargando…</p>
  if (meQuery.isError) {
    return (
      <p role="alert" className="text-red-600">
        No se pudo cargar la cuenta.
      </p>
    )
  }
  const me = meQuery.data

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">Mi cuenta</h1>

      <OrphanedPaymentsNotice />

      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <div className={`${ROW} border-b border-slate-100`}>
          <span className="text-slate-500">Correo</span>
          {/* break-all: an address is one unbroken string and will otherwise
              push the card wider than a phone. */}
          <span className="break-all text-right font-medium">{me.email ?? '—'}</span>
        </div>
        <div className={ROW}>
          <span className="text-slate-500">Saldo</span>
          <span className="font-medium">{formatHours(credit.data?.hours ?? 0)}</span>
        </div>
      </div>

      <div className="space-y-3">
        <Link
          to="/packages"
          className="flex min-h-14 items-center justify-between rounded-lg border border-slate-200 bg-white px-4 font-medium hover:bg-slate-50"
        >
          Comprar horas
          <span aria-hidden="true" className="text-slate-400">
            ›
          </span>
        </Link>

        {/* Only for admins, and only reachable here on a phone -- the header
            link it mirrors is hidden below `md`. */}
        {me.is_admin && (
          <Link
            to="/admin/today"
            className="flex min-h-14 items-center justify-between rounded-lg border border-slate-200 bg-white px-4 font-medium hover:bg-slate-50"
          >
            Ir al local
            <span aria-hidden="true" className="text-slate-400">
              ›
            </span>
          </Link>
        )}
      </div>

      <button
        type="button"
        onClick={handleSignOut}
        className="min-h-12 w-full rounded-md border border-slate-300 px-4 hover:bg-slate-100"
      >
        Cerrar sesión
      </button>
    </div>
  )
}
